import axios from 'axios';
import authHeader from './authHeader';

const endpointBase = process.env.REACT_APP_API_ENDPOINT || window.location.origin + '/api';

export const isDocumentMediaPath = (path) => {
    return typeof path === 'string' && path.includes('documents/media');
};

export const extractDocumentMediaId = (path) => {
    if (!path) {
        return null;
    }
    const match = String(path).match(/\/documents\/media\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
};

export const getDocumentMediaSpaHref = (idOrPath, options = {}) => {
    const { download = false } = options;
    const id = isDocumentMediaPath(idOrPath) ? extractDocumentMediaId(idOrPath) : idOrPath;
    if (!id) {
        return null;
    }
    const href = `/documents/media/${encodeURIComponent(id)}`;
    return download ? `${href}?download=1` : href;
};

export const resolveDocumentMediaApiUrl = (path) => {
    if (!path) {
        return null;
    }
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const base = endpointBase.replace(/\/$/, '');

    if (normalizedPath.startsWith('/api/')) {
        if (base.endsWith('/api')) {
            return `${base.replace(/\/api$/, '')}${normalizedPath}`;
        }
        return `${base}${normalizedPath}`;
    }

    if (base.endsWith('/api')) {
        return `${base}${normalizedPath}`;
    }

    return `${base}${normalizedPath}`;
};

const parseContentDispositionFilename = (contentDisposition, fallback = 'download') => {
    if (!contentDisposition) {
        return fallback;
    }

    const filenameStar = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (filenameStar) {
        return decodeURIComponent(filenameStar[1]);
    }

    const filename = /filename="?([^";\n]+)"?/i.exec(contentDisposition);
    if (filename) {
        return filename[1].replace(/"/g, '');
    }

    return fallback;
};

const parseBlobErrorMessage = async (blob) => {
    try {
        const text = await blob.text();
        const json = JSON.parse(text);
        return json.message || json.errorMessage || json.error || 'Download failed';
    } catch {
        return 'Download failed';
    }
};

const triggerBlobDownload = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const resolveMediaRequestUrl = (pathOrId) => {
    if (isDocumentMediaPath(pathOrId)) {
        return resolveDocumentMediaApiUrl(pathOrId);
    }
    const base = endpointBase.replace(/\/$/, '');
    return `${base}/documents/media/${encodeURIComponent(pathOrId)}`;
};

const resolveMediaFallbackId = (pathOrId) => {
    return extractDocumentMediaId(pathOrId) || pathOrId;
};

const toTypedBlob = (blob, contentType) => {
    if (blob.type && blob.type !== 'application/octet-stream') {
        return blob;
    }
    return new Blob([blob], { type: contentType || 'application/octet-stream' });
};

export async function fetchDocumentMediaBlob(pathOrId) {
    const url = resolveMediaRequestUrl(pathOrId);
    const fallbackId = resolveMediaFallbackId(pathOrId);

    try {
        const res = await axios({
            url,
            method: 'GET',
            headers: authHeader(),
            responseType: 'blob',
        });

        const contentType =
            res.headers['content-type']?.split(';')[0]?.trim() ||
            res.data.type ||
            'application/octet-stream';
        const fileName = parseContentDispositionFilename(
            res.headers['content-disposition'],
            `media-${fallbackId}`
        );
        const blob = new Blob([res.data], { type: contentType });

        return { blob, contentType, fileName };
    } catch (err) {
        if (err?.response?.data instanceof Blob) {
            const message = await parseBlobErrorMessage(err.response.data);
            throw new Error(message);
        }

        const message =
            err?.response?.data?.message ||
            err?.message ||
            'Download failed';

        throw new Error(message);
    }
}

/**
 * Navigates the current tab to a blob URL so the browser native viewer can preview or offer download.
 */
export async function openDocumentMediaInBrowser(pathOrId) {
    const { blob, contentType } = await fetchDocumentMediaBlob(pathOrId);
    const typedBlob = toTypedBlob(blob, contentType);
    const url = window.URL.createObjectURL(typedBlob);
    window.location.replace(url);
}

export async function downloadDocumentMedia(id) {
    try {
        const { blob, contentType, fileName } = await fetchDocumentMediaBlob(id);
        const typedBlob = toTypedBlob(blob, contentType);
        triggerBlobDownload(typedBlob, fileName);
    } catch (err) {
        throw err instanceof Error ? err : new Error(String(err));
    }
}
